import { EMAIL_REGEX } from "./email"

// Mirrors QrCode#normalize_destination_url on the server: a bare host like
// "myrestaurant.com/menu" gets https:// prepended, while anything that already
// starts with a scheme (http://, https://, mailto:, javascript: …) is left
// untouched so validateDestinationUrl can accept (http, https, mailto) or reject it.
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:/i

export function normalizeDestinationUrl(raw) {
  const value = (raw || "").trim()
  if (!value) return ""
  return SCHEME_PREFIX.test(value) ? value : `https://${value}`
}

const HTTP_URL_REGEX = /^https?:\/\//i
const MAX_URL_LENGTH = 2048
const NOT_A_WEB_ADDRESS = "That doesn't look like a web address. Try something like yoursite.com."

export const EMAIL_DESTINATION_ERROR = "That's an email address, not a link."

// Mirrors QrCode.real_host?: normalizeDestinationUrl turns any word into
// https://<word>, so "TBC" would make a code that opens a browser error. A
// real host has a dot and a letters-only last label, or is an IP literal. Read
// with a pattern, as the server does, not new URL(), which rewrites IDN hosts.
const HTTP_HOST = /^https?:\/\/(?:[^/?#@]*@)?(\[[^\]/?#]*\]|[^/?#:@[\]]+)/i
const IPV4_HOST = /^\d{1,3}(?:\.\d{1,3}){3}$/
const TOP_LEVEL_LABEL = /^(?:\p{L}[\p{L}\p{M}]+|xn--[a-z0-9-]+)$/iu

export function hasRealHost(normalized) {
  const match = HTTP_HOST.exec(normalized)
  const host = (match ? match[1] : "").replace(/\.$/, "")
  if (host.startsWith("[")) return true
  if (IPV4_HOST.test(host)) return host.split(".").every((octet) => Number(octet) <= 255)
  const labels = host.split(".")
  return labels.length >= 2 && labels.every((label) => label !== "") &&
    TOP_LEVEL_LABEL.test(labels[labels.length - 1])
}

// Mirrors QrCode.email_like_destination?: "name@hotmail.com" normalizes to
// "https://name@hotmail.com", a working link to hotmail.com's homepage, so
// scanners would land on the mail provider (issue #183). A username, no
// password, and nothing after the host is an email address, not a link.
export function looksLikeEmailDestination(normalized) {
  let url
  try { url = new URL(normalized) } catch { return false }
  return /^https?:$/.test(url.protocol) && url.username !== "" && url.password === "" &&
    url.hostname.includes(".") && (url.pathname === "/" || url.pathname === "") &&
    url.search === "" && url.hash === "" && !/[?#]$/.test(normalized)
}

// Mirrors QrCode.mailto_destination?: every recipient is a real address. The
// server stays authoritative; this is the UX hint.
export function isMailtoDestination(value) {
  const match = /^mailto:([^?\s]+)(\?\S*)?$/i.exec(value)
  if (!match) return false
  // Split before decoding, and require a literal @, as Ruby's parser does:
  // "mailto:name%40example.com" is refused there, so it's refused here.
  return match[1].split(",").every((raw) => {
    const addr = safeDecode(raw)
    return raw.includes("@") && addr !== null && EMAIL_REGEX.test(addr.trim())
  })
}

function safeDecode(value) {
  try { return decodeURIComponent(value) } catch { return null }
}

// What the creator most likely meant by an email-shaped destination: a code
// that opens a new email to it. Offered as a one-click fix, never applied
// silently (docs/plans/2026-09-30-mailto-destinations.plan.md).
export function mailtoSuggestion(normalized) {
  if (!looksLikeEmailDestination(normalized)) return null
  const url = new URL(normalized)
  // A stray % ("na%zz@…") would make decodeURIComponent throw; keep it raw.
  return `mailto:${safeDecode(url.username) ?? url.username}@${url.hostname}`
}

// A destination as a person reads it: no http(s) scheme, no bare trailing
// slash. A mailto: keeps its scheme, which is the only cue that the code opens
// an email and not a page. One function, so every surface that shows "what
// this code opens" shows the same text.
export function displayDestination(url) {
  return (url || "").replace(/^https?:\/\//i, "").replace(/\/$/, "")
}

// Normalize + validate a destination the way the server's QrCode validations
// will. Returns { normalized, error, suggestion }: error is a user-facing
// message when the input isn't an http(s) or mailto: destination or is too
// long, else null; suggestion is a mailto: rewrite to offer when the input was
// an email address. Shared by every destination field so the rule lives in
// one place.
export function validateDestinationUrl(raw) {
  const normalized = normalizeDestinationUrl(raw)
  if (normalized.length > MAX_URL_LENGTH) {
    return { normalized, error: `URLs must be under ${MAX_URL_LENGTH} characters.`, suggestion: null }
  }
  if (/^mailto:/i.test(normalized)) {
    return isMailtoDestination(normalized)
      ? { normalized, error: null, suggestion: null }
      : { normalized, error: "Enter an email address, like you@yoursite.com.", suggestion: null }
  }
  if (!HTTP_URL_REGEX.test(normalized) || !hasRealHost(normalized)) {
    return { normalized, error: NOT_A_WEB_ADDRESS, suggestion: null }
  }
  const suggestion = mailtoSuggestion(normalized)
  if (suggestion) return { normalized, error: EMAIL_DESTINATION_ERROR, suggestion }
  return { normalized, error: null, suggestion: null }
}
